/**
 * CommissionSplitsCard — admin-only two-rep commission split editor on the
 * deal's Money section. Persists to PUT /commissions/deals/{id}/splits;
 * percentages must total 100. Accruals snapshot the split at payment time.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Percent, Loader2 } from "lucide-react";

const FIELD_CLS = "w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white";

export default function CommissionSplitsCard({ dealId, users }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ primary_rep_id: "", primary_rep_pct: 100, secondary_rep_id: "", secondary_rep_pct: 0 });

  useEffect(() => {
    api.get(`/commissions/deals/${dealId}/splits`)
      .then((r) => {
        const d = r.data || {};
        setForm({
          primary_rep_id: d.primary_rep_id || "",
          primary_rep_pct: Number(d.primary_rep_pct ?? 100),
          secondary_rep_id: d.secondary_rep_id || "",
          secondary_rep_pct: Number(d.secondary_rep_pct ?? 0),
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealId]);

  const setPrimaryPct = (v) => {
    const p = Math.min(100, Math.max(0, parseFloat(v) || 0));
    setForm((f) => ({ ...f, primary_rep_pct: p, secondary_rep_pct: f.secondary_rep_id ? Math.round((100 - p) * 100) / 100 : 0 }));
  };
  const setSecondaryPct = (v) => {
    const p = Math.min(100, Math.max(0, parseFloat(v) || 0));
    setForm((f) => ({ ...f, secondary_rep_pct: p, primary_rep_pct: Math.round((100 - p) * 100) / 100 }));
  };

  const total = Number(form.primary_rep_pct || 0) + Number(form.secondary_rep_pct || 0);
  const invalid = form.primary_rep_id && Math.abs(total - 100) > 0.01;

  const save = async () => {
    if (invalid) { toast.error(`Split must total 100% (currently ${total}%)`); return; }
    setSaving(true);
    try {
      await api.put(`/commissions/deals/${dealId}/splits`, {
        primary_rep_id: form.primary_rep_id || null,
        primary_rep_pct: Number(form.primary_rep_pct || 0),
        secondary_rep_id: form.secondary_rep_id || null,
        secondary_rep_pct: form.secondary_rep_id ? Number(form.secondary_rep_pct || 0) : 0,
      });
      toast.success("Commission split saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save split");
    } finally {
      setSaving(false);
    }
  };

  const repOptions = (users || []).filter((u) => ["admin", "manager", "sales"].includes(u.role));

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6" data-testid="commission-splits-card">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-100 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-emerald-700" />
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Commission Split (Admin)</div>
        </div>
        <div className={`text-xs font-bold ${invalid ? "text-red-700" : "text-zinc-500"}`} data-testid="splits-total">
          Total: {total}%
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading split…</div>
      ) : (
        <div className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[200px] flex-1">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Primary Rep</label>
            <select
              value={form.primary_rep_id}
              onChange={(e) => setForm((f) => ({ ...f, primary_rep_id: e.target.value }))}
              data-testid="splits-primary-rep"
              className={FIELD_CLS}
            >
              <option value="">— None (no commission) —</option>
              {repOptions.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div className="w-24">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">%</label>
            <input
              type="number" min="0" max="100" step="5"
              value={form.primary_rep_pct}
              onChange={(e) => setPrimaryPct(e.target.value)}
              data-testid="splits-primary-pct"
              className={`${FIELD_CLS} text-right font-mono`}
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Secondary Rep (optional)</label>
            <select
              value={form.secondary_rep_id}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  secondary_rep_id: v,
                  secondary_rep_pct: v ? f.secondary_rep_pct : 0,
                  primary_rep_pct: v ? f.primary_rep_pct : 100,
                }));
              }}
              data-testid="splits-secondary-rep"
              className={FIELD_CLS}
            >
              <option value="">— None —</option>
              {repOptions.filter((u) => u.id !== form.primary_rep_id).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div className="w-24">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">%</label>
            <input
              type="number" min="0" max="100" step="5"
              value={form.secondary_rep_pct}
              onChange={(e) => setSecondaryPct(e.target.value)}
              disabled={!form.secondary_rep_id}
              data-testid="splits-secondary-pct"
              className={`${FIELD_CLS} text-right font-mono disabled:bg-zinc-50 disabled:text-zinc-400`}
            />
          </div>
          <button
            onClick={save}
            disabled={saving || invalid}
            data-testid="splits-save"
            className="h-9 px-4 text-[10px] font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Split"}
          </button>
        </div>
      )}
      <div className="text-[11px] text-zinc-500 mt-3">
        Applied to every future invoice payment on this project. Splits must total 100%. Past accruals keep the split in effect when they were earned.
      </div>
    </div>
  );
}
